"use client"

import { forwardRef, useId, type ComponentProps, type PropsWithChildren, type Ref } from "react"
import { Close, Content, Overlay, Portal } from "@radix-ui/react-dialog"
import { clsx } from "clsx"

import type { PropsWithClassName } from "~/@types/generics"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"

import * as css from "./styles.css"

export function DrawerClose({
  className,
  children,
  ...props
}: PropsWithClassName<PropsWithChildren<ComponentProps<typeof Close>>>) {
  const t = useTranslate()

  if (children) {
    return (
      <Close className={className} {...props}>
        {children}
      </Close>
    )
  }

  return (
    <Close className={clsx(className)} aria-label={t("aria_panel_close")} {...props}>
      {children}
    </Close>
  )
}

function ForwardedRefDrawerContent(
  { className, children, ...props }: PropsWithClassName<PropsWithChildren<{}>>,
  ref: Ref<HTMLDivElement>
) {
  const id = useId()

  return (
    <Portal key={id}>
      <Overlay className={clsx(css.overlay, css.fixedPart)} />
      <div className={clsx(css.wrapper, css.fixedPart)}>
        <Content ref={ref} className={clsx(css.content, className)} {...props}>
          {children}
        </Content>
      </div>
    </Portal>
  )
}

export const DrawerContent = forwardRef(ForwardedRefDrawerContent)

export { Root as Drawer, Title as DrawerTitle, Trigger as DrawerTrigger } from "@radix-ui/react-dialog"
